import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import axios from "axios";
import * as constants from "../api/constants";
import { WELCOME_CLOSED_EVENT } from "./WelcomeOverlay";

/**
 * Welcome promo popup — opens after the Welcome overlay closes (or short fallback when welcome is
 * suppressed for the current route or disabled by SUPER_ADMIN). Captures lead details, then
 * reveals the discount code unless the same phone already redeemed it.
 *
 * Visual design mimics a cream/burnt-orange paper ticket inspired by the customer-supplied
 * reference: an offer panel on the left ("Special Deal" eyebrow + "GET FLAT X OFF" + min-order
 * footnote + a vertical PARTY BOSS stripe on the right edge of the panel) and the lead-capture
 * form on the right.
 *
 * Suppression rules (skipped when `maintenanceMode`: maintenance page always shows the offer):
 *   - localStorage `partyboss_promo_lead_v3` carries `{ promoId, at }`. The popup is suppressed
 *     for 7 days OR until the popup-default promo code id changes (so admins can rotate offers
 *     and existing customers see the new one without manually clearing storage).
 *   - URL param `?promo=reset` wipes the flag — handy for QA / admin demos.
 */
/** Dispatch on `window` to reopen the promo modal (e.g. from the maintenance page). */
export const OPEN_PROMO_POPUP_EVENT = "partyboss:open-promo-popup";

const SHOWN_KEY = "partyboss_promo_lead_v3";
const SUPPRESS_FOR_MS = 7 * 24 * 60 * 60 * 1000; // 7 days for promo-backed popup
const SUPPRESS_LEAD_ONLY_MS = 2 * 24 * 60 * 60 * 1000; // 2 days for callback-only popup
/**
 * Long enough to comfortably outlast the WelcomeOverlay (6 s display + 1.25 s exit fade) so the
 * popup never tries to open while the welcome splash is still on screen. When the welcome flash
 * is OFF, the overlay dispatches `WELCOME_CLOSED_EVENT` immediately, so the popup still opens
 * promptly without waiting on this fallback.
 */
const FALLBACK_DELAY_MS = 9000;

function isHomePath(pathname) {
  return pathname === "/" || pathname === "/Home";
}

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function isValidPhone(value) {
  return /^\d{10}$/.test(digitsOnly(value));
}

function isValidEmail(value) {
  if (!value) return true; // optional
  return /^\S+@\S+\.\S+$/.test(String(value).trim());
}

function readShownFlag() {
  try {
    const raw = localStorage.getItem(SHOWN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearShownFlag() {
  try {
    localStorage.removeItem(SHOWN_KEY);
  } catch {
    /* ignore */
  }
}

/** Returns true when the popup should stay hidden for `promoId` (null = lead-only mode). */
function isSuppressed(promoId) {
  const flag = readShownFlag();
  if (!flag) return false;
  const flagId = flag.promoId == null ? null : Number(flag.promoId);
  const askId = promoId == null ? null : Number(promoId);
  // Anytime the promo state changed (promo→leadOnly, leadOnly→promo, or different code) re-show.
  if (flagId !== askId) return false;
  if (typeof flag.at !== "number") return true;
  const window_ = askId == null ? SUPPRESS_LEAD_ONLY_MS : SUPPRESS_FOR_MS;
  return Date.now() - flag.at < window_;
}

function persistShownFlag(payload) {
  try {
    localStorage.setItem(
      SHOWN_KEY,
      JSON.stringify({ at: Date.now(), ...(payload || {}) })
    );
  } catch {
    /* quota / privacy mode — ignore */
  }
}

function hasResetParam() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("promo") === "reset";
  } catch {
    return false;
  }
}

const MAINTENANCE_FALLBACK_DELAY_MS = 1800;

export default function PromoPopup({ maintenanceMode = false } = {}) {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(false);
  /** True when no active promo exists — popup stays open as a "request a callback" form. */
  const [leadOnlyMode, setLeadOnlyMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [revealed, setRevealed] = useState(null); // { code, discountAmount, alreadyRedeemed, message }
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const triggeredRef = useRef(false);

  const onAdminRoute = location.pathname.startsWith("/admin");
  const onHome = isHomePath(location.pathname);

  // ---- Trigger: home path (normal) or any path when maintenance gate is on ----
  useEffect(() => {
    if (onAdminRoute) return undefined;
    if (!maintenanceMode && !onHome) return undefined;

    if (hasResetParam()) {
      clearShownFlag();
    }

    const open = () => {
      if (triggeredRef.current) return;
      triggeredRef.current = true;
      // eslint-disable-next-line no-console
      console.info("[PromoPopup] trigger fired");
      setShow(true);
    };

    window.addEventListener(WELCOME_CLOSED_EVENT, open);
    const delayMs = maintenanceMode ? MAINTENANCE_FALLBACK_DELAY_MS : FALLBACK_DELAY_MS;
    const fallback = window.setTimeout(open, delayMs);

    return () => {
      window.removeEventListener(WELCOME_CLOSED_EVENT, open);
      window.clearTimeout(fallback);
    };
  }, [onAdminRoute, onHome, maintenanceMode]);

  useEffect(() => {
    if (onAdminRoute) return undefined;
    const onManualOpen = () => {
      triggeredRef.current = false;
      setShow(true);
    };
    window.addEventListener(OPEN_PROMO_POPUP_EVENT, onManualOpen);
    return () => window.removeEventListener(OPEN_PROMO_POPUP_EVENT, onManualOpen);
  }, [onAdminRoute]);

  useEffect(() => {
    if (maintenanceMode) triggeredRef.current = false;
  }, [maintenanceMode]);

  // ---- Fetch the active popup-default code once we are about to open ----
  // IMPORTANT: do NOT include `loading` in the dependency array. We set it inside this effect; if
  // it were a dependency, React would tear down the effect (firing the cleanup → setting
  // `cancelled = true`) the moment we flipped loading on, and the axios callback would always
  // see cancelled and never clear the loading state.
  useEffect(() => {
    if (!show || promo || leadOnlyMode) return;
    let cancelled = false;
    const skipPromoSuppression = maintenanceMode;
    setLoading(true);
    // eslint-disable-next-line no-console
    console.info("[PromoPopup] fetching active popup default…");
    axios
      .get(constants.PROMO_POPUP_ACTIVE)
      .then((res) => {
        if (cancelled) return;
        // 204 -> no active code configured. Stay open as a callback / lead-capture form so the
        // internal team still gets the contact details and can call back the customer.
        if (!res?.data || typeof res.data !== "object") {
          // eslint-disable-next-line no-console
          console.info("[PromoPopup] no active promo configured — switching to lead-only mode.");
          if (!skipPromoSuppression && isSuppressed(null)) {
            // eslint-disable-next-line no-console
            console.info("[PromoPopup] lead-only popup suppressed by localStorage flag.");
            setShow(false);
            return;
          }
          setLeadOnlyMode(true);
          return;
        }
        if (!skipPromoSuppression && isSuppressed(res.data.id)) {
          // eslint-disable-next-line no-console
          console.info(
            "[PromoPopup] suppressed by localStorage flag for the same promo. Visit ?promo=reset to re-show.",
          );
          setShow(false);
          return;
        }
        setPromo(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[PromoPopup] failed to load promo, falling back to lead-only mode:", err?.message || err);
        if (!skipPromoSuppression && isSuppressed(null)) {
          setShow(false);
          return;
        }
        setLeadOnlyMode(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, promo, leadOnlyMode, maintenanceMode]);

  const dismiss = useCallback(() => {
    setShow(false);
    if (!maintenanceMode) {
      persistShownFlag({
        promoId: promo?.id ?? null,
        leadOnly: leadOnlyMode,
        revealed: Boolean(revealed?.code),
      });
    }
  }, [maintenanceMode, promo?.id, leadOnlyMode, revealed?.code]);

  const onSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      setError("");
      const trimmedName = name.trim();
      const phoneDigits = digitsOnly(phone);
      if (!trimmedName || trimmedName.length < 2) {
        setError("Please share your name");
        return;
      }
      if (!isValidPhone(phoneDigits)) {
        setError("Enter a valid 10-digit phone number");
        return;
      }
      if (!isValidEmail(email)) {
        setError("Enter a valid email or leave it blank");
        return;
      }
      setSubmitting(true);
      try {
        const { data } = await axios.post(constants.PROMO_POPUP_LEAD, {
          promoCodeId: promo?.id ?? null,
          code: promo?.code ?? null,
          customerName: trimmedName,
          phoneNumber: phoneDigits,
          email: email.trim() || null,
          source: maintenanceMode
            ? "MAINTENANCE_POPUP"
            : leadOnlyMode
              ? "CALLBACK_REQUEST"
              : "WELCOME_POPUP",
        });
        if (data && data.accepted) {
          setRevealed({
            code: data.code,
            discountAmount: data.discountAmount,
            alreadyRedeemed: Boolean(data.alreadyRedeemed),
            title: data.title,
            message: data.message,
            leadOnly: leadOnlyMode || !data.code,
          });
        } else {
          setError(data?.message || "Could not save your details. Please try again.");
        }
      } catch (err) {
        const msg = err?.response?.data?.message || "Network error. Please try again.";
        setError(msg);
      } finally {
        setSubmitting(false);
      }
    },
    [email, maintenanceMode, name, phone, promo, leadOnlyMode]
  );

  const onCopyCode = useCallback(() => {
    const code = revealed?.code;
    if (!code) return;
    try {
      navigator.clipboard.writeText(code).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      });
    } catch {
      /* ignore */
    }
  }, [revealed?.code]);

  const offerData = useMemo(() => {
    if (leadOnlyMode) {
      return {
        eyebrow: "Talk to us",
        headline: "GET A CALL BACK",
        description: "OUR PARTY EXPERTS WILL HELP PLAN YOUR EVENT",
        minOrder: null,
      };
    }
    const discount = promo?.discountAmount ?? 0;
    const min = Number(promo?.minBookingAmount || 0);
    return {
      eyebrow: "Special Deal",
      headline: discount ? `GET FLAT ${discount} OFF` : promo?.title || "Special Offer",
      description:
        promo?.description ||
        (discount ? "ON YOUR NEXT PARTY BOSS BOOKING" : "EXCLUSIVE WELCOME OFFER"),
      minOrder: min > 0 ? `MIN. ORDER ${min}` : null,
    };
  }, [promo, leadOnlyMode]);

  if (!show || onAdminRoute) return null;

  return (
    <Modal
      show={show}
      onHide={dismiss}
      centered
      keyboard
      className="party-boss-promo-modal-root party-boss-promo-modal-root--ticket"
      contentClassName="party-boss-promo-modal-content border-0 overflow-hidden"
      dialogClassName="party-boss-promo-modal-dialog party-boss-promo-modal-dialog--ticket"
      backdropClassName="party-boss-promo-modal-backdrop"
    >
      <button
        type="button"
        className="party-boss-promo-modal-close-x"
        aria-label="Close"
        onClick={dismiss}
      >
        <span aria-hidden>×</span>
      </button>

      <div className="party-boss-promo-ticket party-boss-promo-ticket--cream">
        <div className="party-boss-promo-offer">
          <div className="party-boss-promo-offer-content">
            <span className="party-boss-promo-offer-eyebrow">{offerData.eyebrow}</span>
            <h2 className="party-boss-promo-offer-headline">{offerData.headline}</h2>
            <p className="party-boss-promo-offer-desc">{offerData.description}</p>
            {offerData.minOrder ? (
              <span className="party-boss-promo-offer-minorder">{offerData.minOrder}</span>
            ) : null}
          </div>
          <div className="party-boss-promo-offer-stripe" aria-hidden>
            <span className="party-boss-promo-offer-stripe-text">PARTY BOSS</span>
          </div>
        </div>

        <div className="party-boss-promo-ticket-divider" aria-hidden />

        <div className="party-boss-promo-ticket-body">
          {loading ? (
            <div className="party-boss-promo-ticket-loading" aria-live="polite">
              Hold on, fetching today's offer…
            </div>
          ) : revealed ? (
            <RevealedState
              revealed={revealed}
              copied={copied}
              onCopy={onCopyCode}
              onClose={dismiss}
              fallbackTitle={promo?.title}
            />
          ) : (
            <LeadForm
              promo={promo}
              leadOnlyMode={leadOnlyMode}
              name={name}
              phone={phone}
              email={email}
              error={error}
              submitting={submitting}
              onName={setName}
              onPhone={setPhone}
              onEmail={setEmail}
              onSubmit={onSubmit}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

function LeadForm({ promo, leadOnlyMode, name, phone, email, error, submitting, onName, onPhone, onEmail, onSubmit }) {
  const title = leadOnlyMode
    ? "Want our team to call you?"
    : promo?.title || "Unlock today's special";
  const subtitle = leadOnlyMode
    ? "Drop your details and our party experts will reach out to help plan and book your event."
    : "Drop your details and we'll reveal a one-time promo code to use at checkout.";
  const cta = leadOnlyMode ? "Request a callback" : "Get my code";
  const fineprint = leadOnlyMode
    ? "We'll only call to help with your booking — no spam."
    : "One-time use per phone. Valid only for new bookings while the offer is live.";
  return (
    <form className="party-boss-promo-ticket-form" onSubmit={onSubmit}>
      <h3 className="party-boss-promo-ticket-title">{title}</h3>
      <p className="party-boss-promo-ticket-sub">{subtitle}</p>

      <label className="party-boss-promo-ticket-label" htmlFor="pb-promo-name">
        Name <span className="text-danger">*</span>
      </label>
      <input
        id="pb-promo-name"
        type="text"
        className="party-boss-promo-ticket-input"
        placeholder="Your name"
        value={name}
        onChange={(e) => onName(e.target.value)}
        autoComplete="name"
        maxLength={120}
        required
      />

      <label className="party-boss-promo-ticket-label" htmlFor="pb-promo-phone">
        Phone (10 digits) <span className="text-danger">*</span>
      </label>
      <input
        id="pb-promo-phone"
        type="tel"
        className="party-boss-promo-ticket-input"
        placeholder="98XXXXXXXX"
        value={phone}
        onChange={(e) => onPhone(e.target.value)}
        inputMode="numeric"
        autoComplete="tel"
        maxLength={15}
        required
      />

      <label className="party-boss-promo-ticket-label" htmlFor="pb-promo-email">
        Email <span className="party-boss-promo-ticket-optional">(optional)</span>
      </label>
      <input
        id="pb-promo-email"
        type="email"
        className="party-boss-promo-ticket-input"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => onEmail(e.target.value)}
        autoComplete="email"
        maxLength={160}
      />

      {error ? <div className="party-boss-promo-ticket-error" role="alert">{error}</div> : null}

      <div className="party-boss-promo-ticket-actions party-boss-promo-ticket-actions--single">
        <button type="submit" className="party-boss-promo-modal-cta" disabled={submitting}>
          {submitting ? "Sending…" : cta}
        </button>
      </div>
      <p className="party-boss-promo-ticket-fineprint">{fineprint}</p>
    </form>
  );
}

function RevealedState({ revealed, copied, onCopy, onClose, fallbackTitle }) {
  if (revealed.alreadyRedeemed) {
    return (
      <div className="party-boss-promo-ticket-result party-boss-promo-ticket-result--used" role="status">
        <i className="bi bi-info-circle-fill party-boss-promo-ticket-result-icon" aria-hidden />
        <h3 className="party-boss-promo-ticket-title">You've already used this offer</h3>
        <p className="party-boss-promo-ticket-sub">
          Looks like this phone number has already redeemed this promo code on a previous booking.
          Watch out for our next flash offer!
        </p>
        <div className="party-boss-promo-ticket-actions party-boss-promo-ticket-actions--single">
          <button type="button" className="party-boss-promo-modal-cta" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    );
  }
  // Lead-only path: no promo code returned, just a thank-you / callback confirmation.
  if (revealed.leadOnly || !revealed.code) {
    return (
      <div className="party-boss-promo-ticket-result" role="status">
        <i className="bi bi-telephone-outbound-fill party-boss-promo-ticket-result-icon" aria-hidden />
        <h3 className="party-boss-promo-ticket-title">Thanks — we'll be in touch!</h3>
        <p className="party-boss-promo-ticket-sub">
          {revealed.message ||
            "Our party experts will reach out to you shortly to help with your booking."}
        </p>
        <div className="party-boss-promo-ticket-actions party-boss-promo-ticket-actions--single">
          <button type="button" className="party-boss-promo-modal-cta" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="party-boss-promo-ticket-result" role="status">
      <i className="bi bi-stars party-boss-promo-ticket-result-icon" aria-hidden />
      <h3 className="party-boss-promo-ticket-title">{revealed.title || fallbackTitle || "Here's your code"}</h3>
      <p className="party-boss-promo-ticket-sub">
        {revealed.message || "Use this code at checkout to save instantly."}
      </p>
      <div className="party-boss-promo-ticket-codechip" aria-live="polite">
        <span className="party-boss-promo-ticket-codechip-label">Code</span>
        <span className="party-boss-promo-ticket-codechip-value">{revealed.code}</span>
        <button
          type="button"
          className="party-boss-promo-ticket-codechip-copy"
          onClick={onCopy}
          aria-label="Copy promo code"
        >
          <i className={`bi ${copied ? "bi-check2" : "bi-clipboard"}`} aria-hidden />
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>
      {revealed.discountAmount ? (
        <p className="party-boss-promo-ticket-fineprint">
          Worth Rs. {revealed.discountAmount} off your next booking.
        </p>
      ) : null}
      <div className="party-boss-promo-ticket-actions party-boss-promo-ticket-actions--single">
        <button type="button" className="party-boss-promo-modal-cta" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}
